/**
 * Facebook browser pixel — firing events, user data re-init.
 *
 * This module handles the "using" side of the FB pixel: sending browser-side
 * events, building FB-compatible parameters, and keeping the canonical URL
 * in sync.
 */

import { state, debugLog } from "./state.js";
import { FB_CUSTOM_DATA_KEYS, DEFAULT_CURRENCY } from "./constants.js";
import type { EventPayload } from "./types.js";
import { recordPixelPayload } from "./types.js";

/** Re-initialize the browser pixel with current userData (after email/phone capture). */
export function reinitPixelUserData(): void {
  if (state.metaWooBrowserExternal) return;
  if (!window.fbq || !state.config || !state.config.pixelId) return;
  window.fbq("init", state.config.pixelId, state.userData);
}

/** Fix canonical URL so FB pixel SDK uses the actual page URL.
 *  Only removes canonical tags that we created (marked with data-tst),
 *  leaving framework-managed canonical tags untouched to avoid React crashes. */
export function syncCanonicalUrl(): void {
  document.querySelectorAll('link[rel="canonical"][data-tst]').forEach(el => el.remove());

  const link = document.createElement("link");
  link.rel = "canonical";
  link.href = window.location.href;
  link.setAttribute("data-tst", "1");
  document.head.appendChild(link);
}

/** Build Facebook pixel parameters from an event payload (value, currency, custom data). */
export function buildFbParams(payload: {
  eventValue?: number;
  currency?: string;
  customData?: Record<string, unknown>;
}): Record<string, unknown> {
  const fbParams: Record<string, unknown> = {};
  if (payload.eventValue != null) {
    fbParams.value = payload.eventValue;
    fbParams.currency = payload.currency || DEFAULT_CURRENCY;
  }
  if (payload.customData) {
    for (const dataKey of FB_CUSTOM_DATA_KEYS) {
      if (payload.customData[dataKey] != null) fbParams[dataKey] = payload.customData[dataKey];
    }
  }
  return fbParams;
}

/** Fire Facebook browser pixel event. */
export function fireFacebookPixel(
  payload: EventPayload,
  eventId: string,
  capturedUrl: string,
): void {
  if (!window.fbq || !window.fbq.callMethod) return;
  if (!state.config || !state.config.pixelId) return;

  try {
    debugLog("fireFbPixel", "capturedUrl=" + capturedUrl, "eventId=" + eventId);

    // Remove only our canonical tags so FB SDK falls back to window.location.href
    document.querySelectorAll('link[rel="canonical"][data-tst]').forEach(el => el.remove());

    const fbParams = buildFbParams(payload);
    // Server-side event_mapping renames must reach the pixel too: Meta
    // dedups on event_name + event_id, so pixel and CAPI need the SAME name
    const renameMap = state.config.platforms?.facebook?.renameMap;
    const fbEventName = renameMap?.[payload.eventName!] || payload.eventName!;
    window.fbq("trackSingle", state.config!.pixelId, fbEventName, fbParams, { eventID: eventId });
    payload.pixelsFired.push("facebook");
    recordPixelPayload(payload, "facebook", { method: "fbq", args: ["trackSingle", state.config!.pixelId, fbEventName, fbParams, { eventID: eventId }] });
    payload.browserPixelFired = true;

    // Restore canonical to current page URL for other scripts
    syncCanonicalUrl();
  } catch {
    /* FB pixel fire failed — CAPI still sends */
  }
}
